import { Chart, ChartConfiguration, ChartOptions, TooltipItem } from "chart.js/auto";

import { comentariosAvances } from "src/app/shared/model/gestion-proyectos/comentariosAvances";
import { Proyecto } from "src/app/shared/model/gestion-proyectos/proyecto";

/*
 * Los indicadores de un proyecto en Gestión de Proyectos: lo que la tarjeta
 * de la lista le agrega a cada proyecto para pintarlo (avance planeado, días,
 * estatus, último seguimiento, riesgos) y las tres gráficas de barras (avance,
 * esfuerzo y riesgos). Los usan la tarjeta (gestion-proyectos) y la pestaña
 * Resumen de la página del proyecto, para que las dos digan lo mismo (plan
 * docs/superpowers/plans/2026-10-13-etapas-y-versiones.md §8).
 *
 * Salieron tal cual de gestion-proyectos.component.ts: mismas reglas, mismos
 * textos y mismos colores. «Hoy» se puede pasar, para las pruebas.
 */

/** Como llegan las fechas del proyecto: texto ISO del servidor (el modelo dice Date). */
export type FechaProyecto = string | number | Date;

/** Estatus del avance del proyecto (el chip de la tarjeta). */
export type EstatusAvance = "Finalizado" | "En Proceso" | "En Tiempo" | "Retrasado" | "";

/** El último seguimiento del proyecto, como lo pinta «Seguimiento reciente». */
export interface ComentarioReciente {
  descripcion: string;
  nombreUsuario: string;
  fecha: string;
}

/** Lo que se le agrega al proyecto para pintarlo. */
export interface IndicadoresProyecto {
  latestComment: ComentarioReciente;
  fechaInicioReal: FechaProyecto;
  fechaFinReal: FechaProyecto;
  /** Días hábiles de la planeación (el nombre viene de la tarjeta). */
  porcentajeAvance: number;
  /** % del tiempo hábil planeado que ya pasó. */
  avancePlaneado: number;
  existenRiesgos: boolean;
  uiDiasTotales: number;
  uiDiasAvancePlaneado: number;
  uiDiasAvanceReal: number;
  uiDateRange: string;
  uiStatus: EstatusAvance;
  uiStatusClass: string;
}

export type ProyectoConIndicadores<T extends Proyecto = Proyecto> = T & IndicadoresProyecto;

/** Una serie de una gráfica de la tarjeta. */
export interface DatosGrafica {
  etiqueta: string;
  colores: string[];
  datos: number[];
}

/** Los datos de una gráfica de la tarjeta (avance, esfuerzo o riesgos). */
export interface Grafica {
  idProyecto: string;
  etiquetas: string[];
  fechaFin?: FechaProyecto;
  datoEstimado?: DatosGrafica;
  datoReal: DatosGrafica;
}

/** Una gráfica de barras horizontales, lista para Chart.js. */
export type ConfigGraficaBarra = ChartConfiguration<"bar", number[], string>;

/** Una tarea del proyecto con sus tiempos (tareas_detalle o tareas). */
interface TareaConTiempos {
  tiempo_inicio?: FechaProyecto;
  tiempo_fin?: FechaProyecto;
}

type ProyectoConTareas = Proyecto & { tareas_detalle?: TareaConTiempos[]; tareas?: TareaConTiempos[] };

export const SIN_COMENTARIOS = "No hay comentarios disponibles.";
const MILISEGUNDOS_POR_DIA = 1000 * 60 * 60 * 24;
const DOMINGO = 0;
const SABADO = 6;
const COLOR_RETRASO = "#DC2626";
const COLORES_RIESGO = ["rgb(218, 39, 39)", "rgb(255, 99, 49)", "rgb(241, 210, 47)", "rgba(22, 160, 52, 0.86)"];
const ESTILO_BARRA = { borderRadius: 999, borderSkipped: false } as const;
const ETIQUETA_REAL = "Real";
const ETIQUETA_ESTIMADO = "Estimado";
const ETIQUETA_ESPERADO = "Esperado";

/* ============================ Decoración ============================ */

/**
 * El proyecto con lo que la tarjeta le agrega para pintarlo: último
 * seguimiento, fechas reales (de sus tareas), días hábiles, avance planeado,
 * días naturales, rango de fechas, si tiene riesgos y el chip de estatus. No
 * toca el original.
 */
export function decorarProyectoParaVista<T extends Proyecto>(proyecto: T, hoy: Date = new Date()): ProyectoConIndicadores<T> {
  const lineaDeTiempo = lineaDeTiempoReal(proyecto);
  const totalDiasHabiles = diasHabiles(proyecto.fechaInicio, proyecto.fechaFin);
  const planeado = avancePlaneado(proyecto.fechaInicio, proyecto.fechaFin, totalDiasHabiles, hoy);
  const diasTotales = diasNaturales(proyecto.fechaInicio, proyecto.fechaFin);
  const estatus = estatusDeAvance(proyecto.avanceReal, planeado);
  return {
    ...proyecto,
    latestComment: comentarioMasReciente(proyecto.comentariosAvances),
    fechaInicioReal: lineaDeTiempo.fechaInicioReal,
    fechaFinReal: lineaDeTiempo.fechaFinReal,
    porcentajeAvance: totalDiasHabiles,
    avancePlaneado: planeado,
    existenRiesgos: tieneRiesgos(proyecto.riesgoCantidad),
    uiDiasTotales: diasTotales,
    uiDiasAvancePlaneado: Math.round((planeado / 100) * diasTotales),
    uiDiasAvanceReal: Math.round(((proyecto.avanceReal || 0) / 100) * diasTotales),
    uiDateRange: rangoDeFechas(proyecto.fechaInicio, proyecto.fechaFin),
    uiStatus: estatus,
    uiStatusClass: claseDeEstatus(estatus),
  };
}

/** El seguimiento con el id más alto; el texto de «sin comentarios» si no hay. */
export function comentarioMasReciente(comentarios: comentariosAvances[] | null | undefined): ComentarioReciente {
  if (!Array.isArray(comentarios) || comentarios.length === 0) {
    return { descripcion: SIN_COMENTARIOS, nombreUsuario: "", fecha: "" };
  }
  let reciente = comentarios[0];
  for (const comentario of comentarios) {
    if ((comentario?.id ?? 0) > (reciente?.id ?? 0)) {
      reciente = comentario;
    }
  }
  return {
    descripcion: reciente?.descripcion || SIN_COMENTARIOS,
    nombreUsuario: reciente?.nombreUsuario || "",
    fecha: reciente?.fecha || "",
  };
}

/** Los seguimientos del más reciente (id más alto) al más viejo, sin tocar el arreglo. */
export function seguimientosRecientesPrimero(comentarios: comentariosAvances[] | null | undefined): comentariosAvances[] {
  return Array.isArray(comentarios) ? [...comentarios].sort((a, b) => (b?.id ?? 0) - (a?.id ?? 0)) : [];
}

/**
 * Inicio y fin reales: el primer inicio y el último fin de sus tareas (ISO);
 * sin tareas, o sin tiempos legibles, las fechas planeadas.
 */
export function lineaDeTiempoReal(proyecto: Proyecto): { fechaInicioReal: FechaProyecto; fechaFinReal: FechaProyecto } {
  const conTareas = proyecto as ProyectoConTareas;
  const tareas = conTareas.tareas_detalle ?? conTareas.tareas ?? [];
  if (!Array.isArray(tareas) || tareas.length === 0) {
    return { fechaInicioReal: proyecto.fechaInicio, fechaFinReal: proyecto.fechaFin };
  }
  const inicio = extremo(tareas.map((t) => instante(t?.tiempo_inicio)), (a, b) => a < b);
  const fin = extremo(tareas.map((t) => instante(t?.tiempo_fin)), (a, b) => a > b);
  return {
    fechaInicioReal: inicio === null ? proyecto.fechaInicio : new Date(inicio).toISOString(),
    fechaFinReal: fin === null ? proyecto.fechaFin : new Date(fin).toISOString(),
  };
}

/** Días de lunes a viernes entre las dos fechas, contando las dos; 0 si alguna no se lee o están al revés. */
export function diasHabiles(inicio: FechaProyecto, fin: FechaProyecto): number {
  const desde = new Date(inicio);
  const hasta = new Date(fin);
  if (Number.isNaN(desde.getTime()) || Number.isNaN(hasta.getTime()) || desde > hasta) {
    return 0;
  }
  return contarDiasHabiles(desde, (dia) => dia <= hasta);
}

/** % de los días hábiles planeados que ya pasaron antes de hoy (tope 100). */
export function avancePlaneado(inicio: FechaProyecto, fin: FechaProyecto, totalDiasHabiles: number, hoy: Date = new Date()): number {
  const desde = new Date(inicio);
  const hasta = new Date(fin);
  if (Number.isNaN(desde.getTime()) || Number.isNaN(hasta.getTime()) || desde > hasta || totalDiasHabiles <= 0) {
    return 0;
  }
  const consumidos = contarDiasHabiles(desde, (dia) => dia < hoy);
  return Math.min(Math.floor((consumidos / totalDiasHabiles) * 100), 100);
}

/** Días naturales entre las dos fechas (redondeo hacia arriba); 0 si alguna no se lee. */
export function diasNaturales(inicio: FechaProyecto, fin: FechaProyecto): number {
  const desde = new Date(inicio);
  const hasta = new Date(fin);
  if (Number.isNaN(desde.getTime()) || Number.isNaN(hasta.getTime())) {
    return 0;
  }
  return Math.ceil((hasta.getTime() - desde.getTime()) / MILISEGUNDOS_POR_DIA);
}

/** «01/06/2026 - 31/10/2026» en es-MX; «Sin fechas» si falta o no se lee alguna. */
export function rangoDeFechas(inicio: FechaProyecto | null | undefined, fin: FechaProyecto | null | undefined): string {
  const desde = inicio ? new Date(inicio) : null;
  const hasta = fin ? new Date(fin) : null;
  if (!desde || !hasta || Number.isNaN(desde.getTime()) || Number.isNaN(hasta.getTime())) {
    return "Sin fechas";
  }
  return `${desde.toLocaleDateString("es-MX")} - ${hasta.toLocaleDateString("es-MX")}`;
}

/** El estatus del chip según el avance real contra el planeado. */
export function estatusDeAvance(real: number, planeado: number): EstatusAvance {
  if (real === 100 && planeado === 100) {
    return "Finalizado";
  }
  if (real >= planeado - 4 && real < planeado - 3) {
    return "En Proceso";
  }
  if (real >= planeado || real >= planeado - 3) {
    return "En Tiempo";
  }
  if (real <= planeado - 10 || planeado > real) {
    return "Retrasado";
  }
  return "";
}

/** El modificador de table-page__status-badge de cada estatus. */
export function claseDeEstatus(estatus: string): string {
  if (estatus === "Finalizado" || estatus === "En Tiempo") {
    return "is-active";
  }
  return estatus === "En Proceso" ? "is-pending" : "is-danger";
}

/** Si alguna severidad tiene riesgos. */
export function tieneRiesgos(cantidades: number[] | null | undefined): boolean {
  return Array.isArray(cantidades) && cantidades.reduce((total, cantidad) => total + cantidad, 0) > 0;
}

/* ============================ Gráficas ============================ */

/** Avance: esperado (planeado) contra real, en %. */
export function datosGraficaAvance(proyecto: Proyecto): Grafica {
  return {
    idProyecto: proyecto.id,
    etiquetas: [ETIQUETA_ESPERADO, ETIQUETA_REAL],
    fechaFin: proyecto.fechaFin,
    datoEstimado: { etiqueta: "% Esperado", datos: [proyecto.avancePlaneado], colores: ["#78B3CE"] },
    datoReal: { etiqueta: "% Real", datos: [proyecto.avanceReal], colores: ["#DF9755"] },
  };
}

/** Esfuerzo: horas estimadas contra horas reales. */
export function datosGraficaEsfuerzo(proyecto: Proyecto): Grafica {
  return {
    idProyecto: proyecto.id,
    etiquetas: [ETIQUETA_ESTIMADO, ETIQUETA_REAL],
    fechaFin: proyecto.fechaFin,
    datoEstimado: { etiqueta: ETIQUETA_ESTIMADO, datos: [proyecto.horasTotales], colores: ["#64748B"] },
    datoReal: { etiqueta: ETIQUETA_REAL, datos: [proyecto.horasTotalesReales], colores: ["#26A699"] },
  };
}

/** Riesgos: cuántos hay de cada severidad del catálogo. */
export function datosGraficaRiesgo(proyecto: Proyecto): Grafica {
  return {
    idProyecto: proyecto.id,
    etiquetas: proyecto.riesgoSeveridad,
    datoReal: { etiqueta: "Cantidad", datos: proyecto.riesgoCantidad, colores: [...COLORES_RIESGO] },
  };
}

/** Avance en rojo si ya pasó la fecha fin y el real va detrás del esperado. */
export function configGraficaAvance(grafica: Grafica, hoy: Date = new Date()): ConfigGraficaBarra {
  const esperado = grafica.datoEstimado?.datos?.[0] ?? 0;
  const real = grafica.datoReal?.datos?.[0] ?? 0;
  const retraso = new Date(grafica.fechaFin) <= hoy && real < esperado;
  return barras([ETIQUETA_ESPERADO, ETIQUETA_REAL], [esperado, real], ["#78B3CE", retraso ? COLOR_RETRASO : "#DF9755"], 16,
    opcionesBarraHorizontal(100, "%"));
}

/** Esfuerzo en rojo si las horas reales pasan de las estimadas. */
export function configGraficaEsfuerzo(grafica: Grafica): ConfigGraficaBarra {
  const estimado = grafica.datoEstimado?.datos?.[0] ?? 0;
  const real = grafica.datoReal?.datos?.[0] ?? 0;
  return barras([ETIQUETA_ESTIMADO, ETIQUETA_REAL], [estimado, real], ["#64748B", real > estimado ? COLOR_RETRASO : "#26A699"], 16,
    opcionesBarraHorizontal(Math.max(estimado, real, 1), " hrs"));
}

/** Riesgos por severidad, con el color de cada una. */
export function configGraficaRiesgo(grafica: Grafica): ConfigGraficaBarra {
  const valores = grafica.datoReal?.datos ?? [];
  return barras(grafica.etiquetas, valores, grafica.datoReal?.colores ?? [], 12, opcionesBarraHorizontal(Math.max(...valores, 1)));
}

/** Barras horizontales sin leyenda, con el eje hasta un 15 % arriba del máximo y el sufijo en el eje y el tooltip. */
export function opcionesBarraHorizontal(maximo: number, sufijo = ""): ChartOptions<"bar"> {
  const fuente = { size: 10, weight: 600 };
  return {
    indexAxis: "y",
    maintainAspectRatio: false,
    responsive: true,
    layout: { padding: { top: 0, right: 0, bottom: 0, left: 0 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (contexto: TooltipItem<"bar">) => `${contexto.label}: ${Number(contexto.raw ?? 0)}${sufijo}`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: maximo <= 1 ? 1 : Math.ceil(maximo * 1.15),
        grid: { color: "rgba(203, 213, 225, 0.45)" },
        ticks: {
          color: "#64748B",
          font: fuente,
          maxTicksLimit: 4,
          padding: 2,
          precision: 0,
          callback: (valor: number | string) => `${valor}${sufijo}`.trim(),
        },
      },
      y: {
        grid: { display: false },
        ticks: { color: "#334155", font: fuente, padding: 4 },
      },
    },
  };
}

/** Pinta la gráfica en el lienzo (id o elemento); si ya había una ahí, la quita antes. */
export function pintarGraficaBarra(lienzo: string | HTMLCanvasElement, config: ConfigGraficaBarra): Chart<"bar", number[], string> {
  Chart.getChart(lienzo)?.destroy();
  return new Chart(lienzo, config);
}

/* ============================ Internas ============================ */

function barras(etiquetas: string[], datos: number[], colores: string[], grosor: number, options: ChartOptions<"bar">): ConfigGraficaBarra {
  return {
    type: "bar",
    data: {
      labels: etiquetas,
      datasets: [{ data: datos, backgroundColor: colores, ...ESTILO_BARRA, maxBarThickness: grosor }],
    },
    options,
  };
}

function contarDiasHabiles(desde: Date, sigue: (dia: Date) => boolean): number {
  let cuenta = 0;
  const cursor = new Date(desde);
  while (sigue(cursor)) {
    const dia = cursor.getDay();
    if (dia !== DOMINGO && dia !== SABADO) {
      cuenta++;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return cuenta;
}

/** El instante (ms) de un tiempo de tarea; null si no hay o no se lee. */
function instante(valor: FechaProyecto | null | undefined): number | null {
  if (!valor) {
    return null;
  }
  const ms = new Date(valor).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** El menor o el mayor (según gana) de los instantes que se leyeron; null si ninguno. */
function extremo(instantes: Array<number | null>, gana: (a: number, b: number) => boolean): number | null {
  let elegido: number | null = null;
  for (const ms of instantes) {
    if (ms !== null && (elegido === null || gana(ms, elegido))) {
      elegido = ms;
    }
  }
  return elegido;
}
